/**
 * MyCopro - Hook useSubscription
 * Vérification de l'état de l'abonnement de la résidence
 * 
 * ✅ CORRIGÉ:
 * - Supporte les 5 statuts: active, expiring, expired, trial, grace
 * - showTrialBanner: true si trial ET daysRemaining <= 10 (visible à partir du 20ème jour)
 * - graceDaysRemaining: jours restants en période de grâce
 * - showBlockingScreen: uniquement quand expired (après grâce)
 * - Utilise le statut Firestore comme source de vérité
 */

import { useMemo } from 'react';
import { useResidenceStore } from '@/stores';

export interface SubscriptionStatus {
  // État de l'abonnement (aligné avec Firestore + Cloud Functions)
  status: 'active' | 'expiring' | 'expired' | 'trial' | 'grace' | 'unknown';

  // Informations
  endDate: Date | null;
  daysRemaining: number | null;
  plan: 'small' | 'medium' | 'large' | 'xlarge' | null;
  autoRenew: boolean; // true = Stripe renouvelle automatiquement → pas d'alerte expiration

  // Flags par statut
  isActive: boolean;
  isExpiring: boolean; // false si autoRenew=true (renouvellement garanti)
  isExpired: boolean;
  isTrial: boolean;
  isGrace: boolean;

  // ✅ Jours restants en période de grâce
  graceDaysRemaining: number | null;

  // Seuils d'avertissement
  showWarningBanner: boolean;   // Bannière pour expiring/expired/grace
  showTrialBanner: boolean;     // ✅ Bannière trial (à partir de J-10)
  showBlockingScreen: boolean;  // Écran de blocage (expired uniquement)

  // Messages
  warningMessage: string | null;
}

const EXPIRING_THRESHOLD_DAYS = 30; // Avertissement 30 jours avant expiration payante
const TRIAL_BANNER_THRESHOLD_DAYS = 10; // Bannière trial visible à J-10
const GRACE_PERIOD_DAYS = 7;

export const useSubscription = (): SubscriptionStatus => {
  const currentResidence = useResidenceStore(state => state.currentResidence);

  return useMemo(() => {
    // ── Pas de résidence = pas d'abonnement ──
    if (!currentResidence) {
      return createDefaultStatus('unknown');
    }

    const subscription = currentResidence.subscription;
    
    // ── Pas d'objet subscription = mode trial implicite ──
    if (!subscription || !subscription.endDate) {
      return {
        ...createDefaultStatus('trial'),
        isActive: true,
        isTrial: true,
        autoRenew: false,
      };
    }

    // ── Calculs de base ──
    const now = new Date();
    const endDate = new Date(subscription.endDate);
    const timeDiff = endDate.getTime() - now.getTime();
    const daysRemaining = Math.ceil(timeDiff / (1000 * 60 * 60 * 24));
    const plan = (subscription.plan as 'small' | 'medium' | 'large' | 'xlarge' | null) || null;
    // autoRenew=true → Stripe renouvelle automatiquement → pas d'alerte d'expiration
    // undefined traité comme true (comportement par défaut d'un abonnement Stripe actif)
    const autoRenew = subscription.autoRenew !== false;

    // ── Déterminer le statut effectif ──
    // On utilise le statut Firestore comme source de vérité,
    // avec un fallback de recalcul si le cron n'a pas encore tourné
    const firestoreStatus = subscription.status;
    
    // ────────────────────────────────────────────────
    // STATUT: TRIAL
    // ────────────────────────────────────────────────
    if (firestoreStatus === 'trial') {
      if (daysRemaining < 0) {
        // Trial expiré mais le cron n'a pas encore mis à jour → traiter comme grâce
        return buildGraceResult(endDate, daysRemaining, plan, subscription.graceStartDate);
      }
      
      const showTrialBanner = daysRemaining <= TRIAL_BANNER_THRESHOLD_DAYS;
      
      return {
        status: 'trial',
        endDate,
        daysRemaining,
        plan,
        autoRenew: false,
        isActive: true,
        isExpiring: false,
        isExpired: false,
        isTrial: true,
        isGrace: false,
        graceDaysRemaining: null,
        showWarningBanner: false,
        showTrialBanner,
        showBlockingScreen: false,
        warningMessage: showTrialBanner
          ? daysRemaining === 0
            ? "Dernier jour d'essai gratuit !"
            : daysRemaining === 1
              ? "Plus qu'1 jour d'essai gratuit"
              : `Il vous reste ${daysRemaining} jours d'essai gratuit`
          : null
      };
    }
    
    // ────────────────────────────────────────────────
    // STATUT: GRACE
    // ────────────────────────────────────────────────
    if (firestoreStatus === 'grace') {
      return buildGraceResult(endDate, daysRemaining, plan, subscription.graceStartDate, autoRenew);
    }
    
    // ────────────────────────────────────────────────
    // STATUT: EXPIRED (après période de grâce)
    // ────────────────────────────────────────────────
    if (firestoreStatus === 'expired') {
      return {
        status: 'expired',
        endDate,
        daysRemaining,
        plan,
        autoRenew: false,
        isActive: false,
        isExpiring: false,
        isExpired: true,
        isTrial: false,
        isGrace: false,
        graceDaysRemaining: null,
        showWarningBanner: true,
        showTrialBanner: false,
        showBlockingScreen: true,
        warningMessage: 'Votre abonnement a expiré. Veuillez contacter votre administrateur pour réactiver l\'accès.'
      };
    }
    
    // ────────────────────────────────────────────────
    // STATUTS: ACTIVE / EXPIRING
    // ────────────────────────────────────────────────
    if (daysRemaining < 0) {
      // Le cron n'a pas encore mis à jour → traiter comme grâce
      return buildGraceResult(endDate, daysRemaining, plan, subscription.graceStartDate, autoRenew);
    }

    if (daysRemaining <= EXPIRING_THRESHOLD_DAYS || firestoreStatus === 'expiring') {
      // Si autoRenew=true : Stripe renouvelle automatiquement → pas d'alerte
      if (autoRenew) {
        return {
          status: 'active',
          endDate,
          daysRemaining,
          plan,
          autoRenew: true,
          isActive: true,
          isExpiring: false,
          isExpired: false,
          isTrial: false,
          isGrace: false,
          graceDaysRemaining: null,
          showWarningBanner: false,
          showTrialBanner: false,
          showBlockingScreen: false,
          warningMessage: null,
        };
      }

      // autoRenew=false : l'abonnement ne sera PAS renouvelé → alerte légitime
      const warningMessage = daysRemaining === 0
        ? "Votre abonnement expire aujourd'hui !"
        : daysRemaining === 1
          ? "Votre abonnement expire demain !"
          : `Votre abonnement expire dans ${daysRemaining} jours.`;

      return {
        status: 'expiring',
        endDate,
        daysRemaining,
        plan,
        autoRenew: false,
        isActive: true,
        isExpiring: true,
        isExpired: false,
        isTrial: false,
        isGrace: false,
        graceDaysRemaining: null,
        showWarningBanner: true,
        showTrialBanner: false,
        showBlockingScreen: false,
        warningMessage,
      };
    }

    // Active et saine
    return {
      status: 'active',
      endDate,
      daysRemaining,
      plan,
      autoRenew,
      isActive: true,
      isExpiring: false,
      isExpired: false,
      isTrial: false,
      isGrace: false,
      graceDaysRemaining: null,
      showWarningBanner: false,
      showTrialBanner: false,
      showBlockingScreen: false,
      warningMessage: null,
    };

  }, [currentResidence?.subscription]);
};

// ============================================================================
// HELPERS INTERNES
// ============================================================================

/**
 * Construit le résultat pour le statut GRACE
 * Utilisé à la fois pour le statut Firestore 'grace' et les fallbacks 
 * quand le cron n'a pas encore tourné
 */
function buildGraceResult(
  endDate: Date,
  daysRemaining: number,
  plan: 'small' | 'medium' | 'large' | 'xlarge' | null,
  graceStartDate?: string,
  autoRenew = false
): SubscriptionStatus {
  // Calculer les jours restants de grâce
  let graceDaysRemaining: number;
  
  if (graceStartDate) {
    const graceStart = new Date(graceStartDate);
    const now = new Date();
    const daysSinceGrace = Math.floor((now.getTime() - graceStart.getTime()) / (1000 * 60 * 60 * 24));
    graceDaysRemaining = Math.max(0, GRACE_PERIOD_DAYS - daysSinceGrace);
  } else {
    // Pas de graceStartDate → estimer depuis endDate
    const daysSinceEnd = Math.abs(daysRemaining);
    graceDaysRemaining = Math.max(0, GRACE_PERIOD_DAYS - daysSinceEnd);
  }
  
  // Si la grâce est terminée, c'est expired
  if (graceDaysRemaining <= 0) {
    return {
      status: 'expired',
      endDate,
      daysRemaining,
      plan,
      autoRenew: false,
      isActive: false,
      isExpiring: false,
      isExpired: true,
      isTrial: false,
      isGrace: false,
      graceDaysRemaining: 0,
      showWarningBanner: true,
      showTrialBanner: false,
      showBlockingScreen: true,
      warningMessage: 'Votre abonnement a expiré. Veuillez contacter votre administrateur pour réactiver l\'accès.'
    };
  }

  // En période de grâce, accès COMPLET
  const warningMessage = graceDaysRemaining === 1
    ? "Dernier jour avant suspension de l'accès !"
    : `Période de grâce : ${graceDaysRemaining} jours restants avant suspension.`;

  return {
    status: 'grace',
    endDate,
    daysRemaining,
    plan,
    autoRenew,
    isActive: true,
    isExpiring: false,
    isExpired: false,
    isTrial: false,
    isGrace: true,
    graceDaysRemaining,
    showWarningBanner: true,
    showTrialBanner: false,
    showBlockingScreen: false,
    warningMessage,
  };
}

/**
 * Crée un statut par défaut (tous les flags à false)
 */
function createDefaultStatus(status: SubscriptionStatus['status']): SubscriptionStatus {
  return {
    status,
    endDate: null,
    daysRemaining: null,
    plan: null,
    autoRenew: false,
    isActive: false,
    isExpiring: false,
    isExpired: false,
    isTrial: false,
    isGrace: false,
    graceDaysRemaining: null,
    showWarningBanner: false,
    showTrialBanner: false,
    showBlockingScreen: false,
    warningMessage: null,
  };
}

// ============================================================================
// EXPORTS UTILITAIRES
// ============================================================================

/**
 * Formater la date d'expiration
 */
export const formatExpirationDate = (date: Date | null): string => {
  if (!date) return 'Non définie';
  return date.toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
};

/**
 * Obtenir la couleur selon le statut
 * ✅ Supporte grace
 */
export const getSubscriptionStatusColor = (status: string): string => {
  switch (status) {
    case 'active':
      return 'text-green-600 bg-green-100';
    case 'expiring':
      return 'text-amber-600 bg-amber-100';
    case 'expired':
      return 'text-red-600 bg-red-100';
    case 'trial':
      return 'text-blue-600 bg-blue-100';
    case 'grace':
      return 'text-orange-600 bg-orange-100';
    default:
      return 'text-gray-600 bg-gray-100';
  }
};

/**
 * Obtenir le libellé selon le statut
 * ✅ Supporte grace
 */
export const getSubscriptionStatusLabel = (status: string): string => {
  switch (status) {
    case 'active':
      return 'Actif';
    case 'expiring':
      return 'Expire bientôt';
    case 'expired':
      return 'Expiré';
    case 'trial':
      return 'Essai gratuit';
    case 'grace':
      return 'Période de grâce';
    default:
      return 'Inconnu';
  }
};

export default useSubscription;
